/* Small helpers shared by the guide, the step builder, the product page's routine, quick add and the ingredient
   drawer (layout/theme.liquid, deferred):
   - ec.money(cents[, trim, bare]): the same text as Liquid's `money` (trim: as `money_without_trailing_zeros`; bare:
     the number without the currency), in the visitor's currency, from the shop's own format (window.ecMoneySample,
     set by the layout).
   - ec.sectionFetch(url, sectionId): a section's HTML (?section_id=), each address fetched once per page view.
   - ec.prefetchOn(el, getArgs) / ec.prefetchWithin(root, selector, getArgs): start that fetch on pointerenter,
     touchstart or focus, so a tap finds it ready.
   - ec.loadScript(src) / ec.loadStyle(href): add an asset once; resolves when it has loaded.
   - ec.nudge(total, el, copy): the line toward free delivery under a routine's total, then what the order gets.
   - ec.perk(total, copy): what the order gets at that total (free delivery, the gift by name), for a list.
   - ec.saveRoutine(button, routine) / ec.paintSaved(button, url): "Gem rutine" (snippets/account-save.liquid), saved on
     the Mit ECOOKING page in the customer account; ec.takeSaveAfterLogin(kind, here): the one save after "Log ind og gem".
   - ec.takeBuy() / ec.soldOutText(): "Køb rutinen" from Mit ECOOKING (koeb=1).
   - ec.searchTerm(handle): the search a product was found through this visit (search overlay or page), for the cart's
     hidden _search_<variant> attribute (cart.js postAdd; owner, 2026-10-28: as the routines are tagged).
   - Front-page tiles: a plain click on a tile follows its button (section-hero-grid). */
(function () {
  const ec = (window.ec = window.ec || {});

  // A native add-to-cart post (cart.js not there yet) returns to this page as the address bar has it, query included
  // (the gift finder's answers, filters): a card fetched as a section carries the fetch's path in its return_to
  // (snippets/atc-return-to.liquid).
  document.addEventListener("submit", (e) => {
    const input = e.target.querySelector && e.target.querySelector('input[name="return_to"]');
    if (input && input.value.endsWith("#cart-open")) input.value = location.pathname + location.search + "#cart-open";
  }, true);

  // --- Search: which products were found through which search, this visit (sessionStorage; nothing leaves the shop).
  // A product link followed from the search overlay or the search page records its handle with the search term; an add
  // from the overlay itself (quick add) records the term for the next add, a minute long.
  const SEARCH_KEY = "ec_search_refs";
  const readRefs = () => {
    try {
      return JSON.parse(sessionStorage.getItem(SEARCH_KEY) || "{}");
    } catch (e) {
      return {};
    }
  };
  const searchQuery = (root) => {
    const input = root && root.querySelector('input[name="q"]');
    const typed = input && input.value.trim();
    if (typed) return typed;
    return location.pathname.indexOf("/search") !== -1 ? (new URLSearchParams(location.search).get("q") || "").trim() : "";
  };
  document.addEventListener(
    "click",
    (event) => {
      const root = event.target.closest && event.target.closest("#searchmodal-drawer, .template-search");
      if (!root) return;
      const q = searchQuery(root.id === "searchmodal-drawer" ? root : document);
      if (!q) return;
      const refs = readRefs();
      const link = event.target.closest('a[href*="/products/"]');
      if (link) {
        const handle = new URL(link.href, location.origin).pathname.split("/products/")[1].split("/")[0];
        refs[handle] = { q: q.slice(0, 80), t: Date.now() };
      } else if (event.target.closest("button")) {
        refs._pending = { q: q.slice(0, 80), t: Date.now() };
      } else return;
      // The 20 newest.
      const kept = Object.entries(refs).sort((a, b) => b[1].t - a[1].t).slice(0, 20);
      try {
        sessionStorage.setItem(SEARCH_KEY, JSON.stringify(Object.fromEntries(kept)));
      } catch (e) {}
    },
    true
  );
  // The term for an add: on the search page its own query; on a product page reached from a search, that search; an add
  // made from the overlay within the last minute, its search.
  ec.searchTerm = () => {
    if (location.pathname.indexOf("/search") !== -1) return searchQuery(document);
    const refs = readRefs();
    const pending = refs._pending;
    if (pending && Date.now() - pending.t < 60000 && document.querySelector("#searchmodal-drawer[open]")) return pending.q;
    const match = location.pathname.match(/\/products\/([^/?#]+)/);
    const ref = match && refs[decodeURIComponent(match[1])];
    return ref ? ref.q : "";
  };

  // --- Front-page tiles (sections/section-hero-grid): a plain click on a tile, outside its links, follows the tile's
  // own link (its button or arrow; on the label card the main button, not the second text link), as the video strip
  // presses its button. The link stays on the button, so the words stay text: a drag selection doesn't navigate, and a
  // mouse click on the words waits a moment, so a double click can still select a word. Ctrl/cmd/shift or the middle
  // button open the link in a new tab, as a link would (middle: no autoscroll on the tile). A tap only navigates.
  const tileLink = (event) => {
    if (event.altKey || event.button > 1 || (window.Shopify && Shopify.designMode)) return null;
    const tile = event.target.closest && event.target.closest(".hero-grid__tile");
    if (!tile || event.target.closest("a, button, input, select, textarea, label")) return null;
    return tile.querySelector(".hero-grid__link a[href]");
  };
  let tileTimer;
  const tileClick = (event) => {
    const link = !event.defaultPrevented && tileLink(event);
    clearTimeout(tileTimer);
    if (!link || event.detail > 1 || String(window.getSelection())) return;
    if (event.button === 1 || event.ctrlKey || event.metaKey || event.shiftKey) {
      event.preventDefault();
      window.open(link.href, "_blank", "noopener");
    } else if (event.type === "click") {
      const onWords = event.pointerType !== "touch" && event.target.closest(".hero-grid__body, .hero-grid__card");
      if (!onWords) return link.click();
      tileTimer = setTimeout(() => String(window.getSelection()) || link.click(), 300);
    }
  };
  document.addEventListener("click", tileClick);
  document.addEventListener("auxclick", tileClick);
  document.addEventListener("mousedown", (event) => event.button === 1 && tileLink(event) && event.preventDefault());

  // "1.000,00 kr" → before "", thousands ".", decimal ",", after " kr"; "1000 kr" (no decimals) → decimals false.
  let shape;
  const moneyShape = () => {
    if (shape) return shape;
    const sample = String(window.ecMoneySample || "1,000.00");
    const m = sample.match(/^([\s\S]*?)1([^0-9]?)000(?:([^0-9])00)?([\s\S]*)$/);
    shape = m
      ? { before: m[1], thousands: m[2], decimal: m[3] || "", decimals: !!m[3], after: m[4] }
      : { before: "", thousands: ",", decimal: ".", decimals: true, after: "" };
    return shape;
  };

  ec.money = (cents, trim, bare) => {
    const s = moneyShape();
    const value = Number(cents) || 0;
    const fixed = (Math.abs(value) / 100).toFixed(s.decimals ? 2 : 0);
    let [whole, frac] = fixed.split(".");
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, s.thousands);
    if (trim && frac === "00") frac = "";
    const number = (value < 0 ? "-" : "") + whole + (frac ? s.decimal + frac : "");
    return bare ? number : s.before + number + s.after;
  };

  const cache = new Map();
  const sectionUrl = (url, sectionId) => {
    const u = new URL(url, location.origin);
    u.searchParams.set("section_id", sectionId);
    return u.pathname + u.search;
  };

  ec.sectionFetch = (url, sectionId) => {
    const key = sectionUrl(url, sectionId);
    if (!cache.has(key)) {
      const request = fetch(key).then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      });
      // A failed fetch is not kept: the next try asks again.
      request.catch(() => cache.delete(key));
      cache.set(key, request);
    }
    return cache.get(key);
  };

  ec.prefetchOn = (el, getArgs) => {
    if (!el || el.ecPrefetch) return;
    el.ecPrefetch = true;
    const go = () => {
      const args = getArgs();
      if (args) ec.sectionFetch(...args).catch(() => {});
    };
    ["pointerenter", "touchstart", "focusin"].forEach((type) => el.addEventListener(type, go, { passive: true }));
  };

  // The same for every link matching selector inside root, present or added later (one listener per event).
  ec.prefetchWithin = (root, selector, getArgs) => {
    if (!root) return;
    const go = (event) => {
      const el = event.target.closest && event.target.closest(selector);
      if (!el || !root.contains(el)) return;
      const args = getArgs(el);
      if (args) ec.sectionFetch(...args).catch(() => {});
    };
    ["pointerover", "touchstart", "focusin"].forEach((type) => root.addEventListener(type, go, { passive: true }));
  };

  const loads = new Map();
  const load = (key, make) => {
    if (!loads.has(key)) {
      loads.set(
        key,
        new Promise((resolve, reject) => {
          const el = make();
          el.onload = resolve;
          el.onerror = () => {
            loads.delete(key);
            reject(new Error(key));
          };
          document.head.append(el);
        })
      );
    }
    return loads.get(key);
  };

  ec.loadScript = (src) =>
    document.querySelector(`script[src="${src}"]`) && !loads.has(src)
      ? Promise.resolve()
      : load(src, () => Object.assign(document.createElement("script"), { src, defer: true }));

  ec.loadStyle = (href) =>
    document.querySelector(`link[href="${href}"]`) && !loads.has(href)
      ? Promise.resolve()
      : load(href, () => Object.assign(document.createElement("link"), { rel: "stylesheet", href }));

  // The nudge, from the cart's thresholds (snippets/routine-build-thresholds.liquid) and the routine's total after its
  // discount plus what is in the cart: what is left to free delivery; once reached, what the order gets, the gift by
  // name and value. Nothing about a gift before it is reached (owner, 2026-10-20). copy.done false: only the way to
  // free delivery (the builder's bar, which lists what is reached in "Din rutine" instead: ec.perk).
  const thresholds = () => {
    try {
      const data = document.querySelector("[data-cart-thresholds]");
      return data ? JSON.parse(data.textContent) : null;
    } catch (e) {
      return null;
    }
  };

  // What the order gets at this total: free delivery and/or the gift, as one line ("" when neither).
  ec.perk = (total, copy) => {
    const t = thresholds();
    if (!t || total <= 0) return "";
    const sum = total + (t.cart || 0);
    const ship = t.shipping > 0 && sum >= t.shipping;
    const gift = t.gift > 0 && t.gift_name && sum >= t.gift;
    const fill = (s) => (s || "").replace("[gift]", t.gift_name).replace("[value]", ec.money(Math.round((t.gift_value || 0) / 100) * 100, true));
    if (gift) return fill(ship ? copy.both : copy.gift_done);
    return ship ? copy.shipping_done || "" : "";
  };

  ec.nudge = (total, el, copy) => {
    if (!el) return;
    const t = thresholds();
    let text = "";
    if (t && total > 0) {
      const sum = total + (t.cart || 0);
      if (t.shipping > 0 && sum < t.shipping) text = (copy.shipping || "").replace("[amount]", ec.money(t.shipping - sum, true));
      else if (copy.done !== false) text = ec.perk(total, copy);
    }
    el.textContent = text;
    el.hidden = !text;
  };

  // "Gem rutine". Logged out: log in and come back to this very result (the page's own address, which still holds every
  // answer, so the guide doesn't ask again; the saved url leaves the pregnancy answer out); one tap then saves.
  // Logged in: the Mit ECOOKING page in the customer account (the ECOOKING Account app) with the routine in its address
  // (?gem=, base64url JSON). It saves on arrival to the customer's own custom.saved_routines (no server of our own).
  // here: the result's own address with every answer (default: this page's; the guide in a panel over a collection keeps
  // its answers out of the address).
  ec.saveRoutine = (button, routine, here = location.pathname + location.search) => {
    if (!button || !routine || !routine.url) return;
    if (!button.dataset.loggedIn) {
      // Back from the login, the result saves itself: one tap, not two (owner 2026-10-29). Ten minutes, this tab only.
      try {
        sessionStorage.setItem("ec_save_after_login", JSON.stringify({ kind: routine.kind || "", here, t: Date.now() }));
      } catch (e) {}
      location.href = "/customer_authentication/login?return_to=" + encodeURIComponent(here);
      return;
    }
    // back: this very result with every answer, only for "Tilbage til rutinen" (never saved; routine.url leaves the pregnancy answer out).
    const bytes = new TextEncoder().encode(JSON.stringify({ v: 1, ...routine, back: here }));
    let binary = "";
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    const gem = btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    location.href = button.dataset.accountSave + "?gem=" + gem;
  };

  // A result's link for comparing: its path and answers, sorted and decoded, without what is never saved or only marks
  // the visit (gravid, avit, gemt, back, view, preview and Shopify's or campaign parameters).
  const SKIP = new Set(["gravid", "avit", "gemt", "back", "view", "preview_theme_id", "fbclid", "gclid"]);
  const norm = (url) => {
    try {
      const u = new URL(url, location.origin);
      const params = [...u.searchParams].filter(([k]) => !SKIP.has(k) && !/^(shop_|utm_|_)/.test(k)).map(([k, v]) => k + "=" + v).sort();
      return u.pathname + "?" + params.join("&");
    } catch (e) {
      return String(url);
    }
  };

  // Saved: the button gives way to "Gemt · Se i din profil", a link to Mit ECOOKING (logged in only). off: no save at all
  // here (gift ideas).
  const paint = (button, saved, off) => {
    const view = button.nextElementSibling && button.nextElementSibling.matches("[data-account-save-view]") ? button.nextElementSibling : null;
    button.hidden = !!off || (saved && !!view);
    if (view) view.hidden = !!off || !saved;
  };

  // Saved when this result is among the customer's saved routines (read by Liquid), compared
  // by its answers, not the exact text of the link. Also right after Mit ECOOKING saved it: its "Tilbage til rutinen" adds
  // gemt=1, because the shop's page can still show the list from before the save for a moment.
  ec.paintSaved = (button, url, off) => {
    if (!button) return;
    let saved = false;
    try {
      saved = JSON.parse(button.dataset.savedUrls || "[]").some((u) => norm(u) === norm(url));
    } catch (e) {}
    if (!saved && button.dataset.loggedIn && new URLSearchParams(location.search).get("gemt") === "1" && norm(location.pathname + location.search) === norm(url)) saved = true;
    paint(button, saved, off);
  };
  // Back from "Log ind og gem": true once, and only on the result that asked (same kind, guide or builder, and the same
  // answers), within ten minutes. Another result leaves the marker for its own page; an old one is cleared.
  ec.takeSaveAfterLogin = (kind, here) => {
    const KEY = "ec_save_after_login";
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(KEY) || "null");
    } catch (e) {}
    if (!pending) return false;
    // Every answer counts here (gravid and avit too: they change the products), only Shopify's and campaign parameters
    // and the markers don't.
    const exact = (url) => {
      try {
        const u = new URL(url, location.origin);
        const params = [...u.searchParams].filter(([k]) => !["gemt", "back", "preview_theme_id", "fbclid", "gclid"].includes(k) && !/^(shop_|utm_|_)/.test(k)).map(([k, v]) => k + "=" + v).sort();
        return u.pathname + "?" + params.join("&");
      } catch (e) {
        return String(url);
      }
    };
    const fresh = Date.now() - (pending.t || 0) < 600000;
    const mine = pending.kind === kind && exact(pending.here || "") === exact(here);
    if (!fresh || mine) {
      try {
        sessionStorage.removeItem(KEY);
      } catch (e) {}
    }
    return fresh && mine;
  };
  // "Køb rutinen" from Mit ECOOKING: the routine's page with koeb=1, which puts the saved routine in the cart as
  // "Læg rutinen i kurven" does (its _routine line attribute gives the routine discount; a cart link can't set it).
  // True once: the marker leaves the address at once, so a reload doesn't add again, and the same address within a
  // minute doesn't add twice (sessionStorage).
  ec.takeBuy = () => {
    const url = new URL(location.href);
    if (url.searchParams.get("koeb") !== "1") return false;
    url.searchParams.delete("koeb");
    history.replaceState(history.state, "", url.pathname + url.search + url.hash);
    const key = "ec-koeb:" + url.pathname + url.search;
    try {
      const last = Number(sessionStorage.getItem(key));
      if (last && Date.now() - last < 60000) return false;
      sessionStorage.setItem(key, String(Date.now()));
    } catch (e) {}
    return true;
  };

  // What to say when some of a bought routine's products are sold out (data-buy-sold-out / data-buy-none on the page).
  ec.soldOutText = (el, names, added) => {
    if (!names.length) return "";
    if (!added) return el.dataset.buyNone || "";
    return (el.dataset.buySoldOut || "").replace("[names]", names.join(", "));
  };
})();
